import {
  AuthorizationInterface,
  LineItemsType,
  OrganizationInterface,
  VoucherInterface,
} from "../types";
import { DEFAULT_VOUCHERS_PER_PAGE } from "./constants";

class GlobalSharedStateClass {
  private userCache = localStorage.getItem("user");
  private USER: null | string =
    (this.userCache && JSON.parse(this.userCache)?.email) || "";
  private CACHED_TABLE_COLUMNS = `${this.USER}-cached-table-columns`;
  private SELECTED_ORGANIZATIONS = `${this.USER}-selected-organizations`;
  private ORGANIZATIONS_SORT = `${this.USER}-organizations-sort`;
  private ZOOM_LEVEL = `${this.USER}-zoom-level`;
  static hasTokenInUrl: boolean = false;

  private getFromCache(key: string) {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : undefined;
  }

  setHasTokenInUrl(value: boolean) {
    GlobalSharedStateClass.hasTokenInUrl = value;
  }

  getHasTokenInUrl() {
    return GlobalSharedStateClass.hasTokenInUrl;
  }

  setCacheTableColumns(data: string[]) {
    localStorage.setItem(this.CACHED_TABLE_COLUMNS, JSON.stringify(data));
  }

  getCacheTableColumns(): string[] | undefined {
    return this.getFromCache(this.CACHED_TABLE_COLUMNS);
  }

  setCacheSelectedOrganizations(data: OrganizationInterface[] | undefined) {
    localStorage.setItem(this.SELECTED_ORGANIZATIONS, JSON.stringify(data));
  }

  getCacheSelectedOrganizations(): OrganizationInterface[] | undefined {
    return this.getFromCache(this.SELECTED_ORGANIZATIONS);
  }

  clearSelectedOrganizations(): void {
    localStorage.removeItem(this.SELECTED_ORGANIZATIONS);
  }

  setOrganizationSort(value: string): void {
    localStorage.setItem(this.ORGANIZATIONS_SORT, value);
  }

  getOrganizationSort(): string | null {
    return localStorage.getItem(this.ORGANIZATIONS_SORT);
  }

  clearSelectedOrganizationsSort(): void {
    localStorage.removeItem(this.ORGANIZATIONS_SORT);
  }

  setZoomLevel(value: number): void {
    localStorage.setItem(this.ZOOM_LEVEL, value.toString());
  }

  getZoomLevel(): number | undefined {
    const value = localStorage.getItem(this.ZOOM_LEVEL);
    return value ? Number(value) : undefined;
  }

  clearZoomLevel(): void {
    localStorage.removeItem(this.ZOOM_LEVEL);
  }

  getCachedVoucher(
    id: number
  ): { voucher: VoucherInterface; metaData: LineItemsType } | undefined {
    const cache: string | null = sessionStorage.getItem(
      `voucher-${id}-${this.USER}`
    );
    return cache ? JSON.parse(cache) : undefined;
  }

  setCacheVoucher(
    id: number,
    value: { voucher: VoucherInterface; metaData?: LineItemsType }
  ): void {
    sessionStorage.setItem(`voucher-${id}-${this.USER}`, JSON.stringify(value));
  }

  deleteCachedVoucher(id: number): void {
    sessionStorage.removeItem(`voucher-${id}-${this.USER}`);
  }

  deleteCachedVouchers(): void {
    const voucherKeys = Object.keys(sessionStorage);
    voucherKeys.forEach((key: string) => {
      if (key.includes(this.USER as string)) {
        sessionStorage.removeItem(key);
      }
    });
  }

  setRowsPerPage(rowsNumber: number): void {
    sessionStorage.setItem(`rows-per-page-${this.USER}`, rowsNumber.toString());
  }

  getRowsPerPage(): number {
    const cache: string | null = sessionStorage.getItem(
      `rows-per-page-${this.USER}`
    );
    return cache ? JSON.parse(cache) : DEFAULT_VOUCHERS_PER_PAGE;
  }

  setSelectedStatuses(statuses: string[]): void {
    sessionStorage.setItem(
      `selected-statuses-${this.USER}`,
      JSON.stringify(statuses)
    );
  }

  getSelectedStatuses(): string[] {
    const cache: string | null = sessionStorage.getItem(
      `selected-statuses-${this.USER}`
    );
    return cache ? JSON.parse(cache) : [];
  }

  getUser(): string | null {
    return this.USER;
  }

  setAuthData(data: AuthorizationInterface) {
    localStorage.setItem(`auth-data-${this.USER}`, JSON.stringify(data));
  }

  getAuthData(): AuthorizationInterface | undefined {
    const cache: string | null = localStorage.getItem(`auth-data-${this.USER}`);
    return cache ? JSON.parse(cache) : undefined;
  }
}

export default GlobalSharedStateClass;
